import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Power, X, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = ts => ts
  ? new Date(ts).toLocaleString('sv-SE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

const emptyForm = {
  name: '', description: '', guidance: '', is_active: true,
};

export default function NewsletterTemplatesTab() {
  const { isAdmin } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);   // template-objekt vid redigering, null vid skapande
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const authHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session?.access_token || ''}`,
    };
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/newsletter/templates?include_inactive=true`, {
        headers: await authHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setTemplates(data.templates || []);
    } catch (e) {
      toast.error(`Kunde inte hämta mallar: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || '',
      description: t.description || '',
      guidance: t.guidance || '',
      is_active: !!t.is_active,
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.guidance.trim()) {
      toast.error('Namn och vägledning krävs.');
      return;
    }
    setSaving(true);
    try {
      const url = editing
        ? `${API}/api/newsletter/templates/${editing.id}`
        : `${API}/api/newsletter/templates`;
      const r = await fetch(url, {
        method: editing ? 'PATCH' : 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${(await r.text()).slice(0, 150)}`);
      toast.success(editing ? 'Mall uppdaterad.' : 'Mall skapad.');
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(`Spara misslyckades: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t) => {
    try {
      const r = await fetch(`${API}/api/newsletter/templates/${t.id}`, {
        method: 'PATCH',
        headers: await authHeaders(),
        body: JSON.stringify({ is_active: !t.is_active }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setTemplates(prev => prev.map(x => x.id === t.id ? { ...x, is_active: !t.is_active } : x));
    } catch (e) {
      toast.error(`Kunde inte ändra status: ${e.message}`);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Ta bort mallen "${t.name}"?`)) return;
    try {
      const r = await fetch(`${API}/api/newsletter/templates/${t.id}`, {
        method: 'DELETE',
        headers: await authHeaders(),
      });
      if (!r.ok) {
        if (r.status === 403) throw new Error('Endast admin kan ta bort mallar.');
        throw new Error(`HTTP ${r.status}`);
      }
      setTemplates(prev => prev.filter(x => x.id !== t.id));
      toast.success('Borttagen.');
    } catch (e) {
      toast.error(`Borttagning misslyckades: ${e.message}`);
    }
  };

  return (
    <div data-testid="newsletter-templates-tab">
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16,
      }}>
        <p style={{ fontSize: 13, color: '#6b7280', margin: 0, maxWidth: 640 }}>
          Skapa mallar med vägledning som AI:n följer vid generering — struktur, tonalitet, fraser eller exempel.
          Mallar är valbara per generering i fliken <strong>Skapa nyhetsbrev</strong> och fungerar för alla tre modeller (Gemini, Claude, GPT-4o).
        </p>
        <button
          className="generate-btn generate-btn--active"
          onClick={openCreate}
          data-testid="btn-add-template"
          style={{ width: 'auto', minWidth: 180 }}
        >
          <Plus size={14} /> Lägg till mall
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
          <Loader2 size={20} className="spin" /> Laddar...
        </div>
      ) : (
        <table className="data-table" data-testid="templates-table">
          <thead>
            <tr>
              <th>Namn</th>
              <th>Beskrivning</th>
              <th>Skapad av</th>
              <th>Uppdaterad</th>
              <th style={{ textAlign: 'center' }}>Aktiv</th>
              <th style={{ textAlign: 'right' }}>Åtgärder</th>
            </tr>
          </thead>
          <tbody>
            {templates.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
                Inga mallar än. Klicka på "Lägg till mall" för att skapa en.
              </td></tr>
            )}
            {templates.map(t => (
              <tr key={t.id} data-testid={`template-row-${t.id}`}
                  style={{ opacity: t.is_active ? 1 : 0.55 }}>
                <td><strong>{t.name}</strong></td>
                <td style={{ color: '#6b7280', fontSize: 13 }}>{t.description || '—'}</td>
                <td style={{ fontSize: 12, color: '#6b7280' }}>{t.created_by || '—'}</td>
                <td style={{ fontSize: 12, color: '#6b7280' }}>{fmtDate(t.updated_at)}</td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    onClick={() => toggleActive(t)}
                    data-testid={`template-toggle-${t.id}`}
                    title={t.is_active ? 'Inaktivera' : 'Aktivera'}
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: t.is_active ? '#10b981' : '#9ca3af',
                    }}
                  >
                    <Power size={16} />
                  </button>
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="btn-text"
                    onClick={() => openEdit(t)}
                    data-testid={`template-edit-${t.id}`}
                    title="Redigera"
                    style={{ marginRight: 4 }}
                  >
                    <Edit2 size={14} />
                  </button>
                  <button className="btn-text"
                    onClick={() => remove(t)}
                    data-testid={`template-delete-${t.id}`}
                    title={isAdmin ? 'Ta bort' : 'Endast admin kan ta bort'}
                    disabled={!isAdmin}
                    style={{ opacity: isAdmin ? 1 : 0.4, cursor: isAdmin ? 'pointer' : 'not-allowed' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Form-modal */}
      {showForm && (
        <div
          data-testid="template-form-modal"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => setShowForm(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', padding: 28, borderRadius: 14, width: 680,
              maxWidth: 'calc(100vw - 40px)', maxHeight: 'calc(100vh - 60px)',
              overflowY: 'auto',
              boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ margin: 0 }}>{editing ? 'Redigera mall' : 'Ny mall'}</h3>
              <button
                onClick={() => setShowForm(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: 14 }}>
              <div>
                <label className="field-label">Namn</label>
                <input
                  className="field-input"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="t.ex. Produktlansering – Energisk"
                  data-testid="form-name"
                />
              </div>

              <div>
                <label className="field-label">Beskrivning (valfri)</label>
                <input
                  className="field-input"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Kort intern beskrivning"
                  data-testid="form-description"
                />
              </div>

              <div>
                <label className="field-label">Vägledning till AI</label>
                <textarea
                  className="field-input field-textarea"
                  value={form.guidance}
                  onChange={e => setForm({ ...form, guidance: e.target.value })}
                  placeholder={
                    "Skriv struktur, ton, fraser eller exempel som AI ska följa.\n\n" +
                    "Exempel:\n" +
                    "- Starta alltid subject line med emoji + förmånsord (t.ex. NYHET, EXKLUSIVT)\n" +
                    "- Använd direkt tilltal (du/dig)\n" +
                    "- Lyft fram 1 stor förmån, inte flera\n" +
                    "- CTA: max 2 ord, alltid verb först (Köp nu, Upptäck)\n" +
                    "- Avsluta textblocket med en känslomässig krok"
                  }
                  rows={14}
                  data-testid="form-guidance"
                  style={{ fontFamily: 'inherit', fontSize: 13, lineHeight: 1.5, resize: 'vertical' }}
                />
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={e => setForm({ ...form, is_active: e.target.checked })}
                  data-testid="form-is-active"
                />
                Aktiv (visas i mall-väljaren vid generering)
              </label>
            </div>

            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => setShowForm(false)}
                data-testid="form-cancel"
                style={{
                  background: '#f3f4f6', color: '#374151',
                  border: '1px solid #d1d5db', padding: '10px 18px',
                  borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14,
                }}
              >
                Avbryt
              </button>
              <button
                className="generate-btn generate-btn--active"
                onClick={save}
                disabled={saving}
                data-testid="form-save"
                style={{ width: 'auto', minWidth: 180 }}
              >
                {saving ? <Loader2 size={14} className="spin" /> : null}
                {editing ? 'Spara ändringar' : 'Skapa mall'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
